import type { AiUserPendingGuidance, AiUserSupervisorReason } from '@/lib/api/ai-user/ai-user-api'
import {
    SUPERVISOR_REASON_LABEL,
    supervisorReasonOf,
    useAiUserRunner,
    type AiUserStage,
    type AiUserStatus,
    type DiaryKind,
    type PendingApproval,
} from '@/lib/ai-user/ai-user-runner'
import { useChatDock } from '@/lib/providers/chat-dock-provider'
import { useAuth } from '@/store/hooks'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    MdCheck,
    MdChevronRight,
    MdClose,
    MdOpenInNew,
    MdPause,
    MdPerson,
    MdPlayArrow,
    MdViewKanban,
    MdWarning,
    MdSend,
    MdSettings,
    MdSmartToy,
    MdStop,
    MdManageSearch,
} from 'react-icons/md'
import { requestAttentionPermission, useAttentionPreference, useSupervisorAttention } from './use-supervisor-attention'

const STATUS_LABEL: Record<AiUserStatus, string> = {
    idle: 'Parado',
    running: 'Trabalhando',
    pausing: 'Pausando',
}

const STATUS_BADGE: Record<AiUserStatus, string> = {
    idle: 'badge-ghost',
    running: 'badge-success',
    pausing: 'badge-warning',
}

const STAGE_LABEL: Record<AiUserStage, string> = {
    opening: 'Abrindo a conversa',
    thinking: 'Lendo e decidindo',
    working: 'Montando o agendamento',
    moving: 'Movendo a oportunidade no funil',
    typing: 'Escrevendo a resposta',
    awaiting_approval: 'Aguardando sua aprovação',
    sending: 'Enviando',
}

const DIARY_DOT: Record<DiaryKind, string> = {
    info: 'bg-base-content/30',
    decision: 'bg-info',
    action: 'bg-success',
    warning: 'bg-warning',
    error: 'bg-error',
}

const timeFormat = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
})

function conversationLabel(chatId: number, chatName?: string): string {
    return chatName ? `${chatName} (#${chatId})` : `Conversa #${chatId}`
}

/** Relógio da gaveta: o "há quanto tempo espera" anda sozinho. */
function useNow(intervalMs = 30_000): number {
    const [now, setNow] = useState(() => Date.now())
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), intervalMs)
        return () => clearInterval(timer)
    }, [intervalMs])
    return now
}

function waitedFor(since: number, now: number): string {
    const minutes = Math.max(0, Math.floor((now - since) / 60_000))
    if (minutes < 1) return 'agora'
    if (minutes < 60) return `há ${minutes} min`
    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60
    return rest ? `há ${hours} h ${String(rest).padStart(2, '0')}` : `há ${hours} h`
}

/** Espera longa ganha cor: o contato costuma cobrar depois de uns 30 minutos. */
function waitTone(since: number, now: number): string {
    const minutes = (now - since) / 60_000
    if (minutes >= 30) return 'text-error'
    if (minutes >= 10) return 'text-warning'
    return 'text-base-content/60'
}

function OpenChatButton({ chatId }: { chatId: number }) {
    const { openDock, openChat } = useChatDock()
    return (
        <button
            type="button"
            className="btn btn-ghost btn-xs btn-square shrink-0"
            aria-label="Abrir a conversa"
            title="Abrir a conversa"
            onClick={() => {
                openDock()
                openChat(chatId)
            }}
        >
            <MdOpenInNew />
        </button>
    )
}

function FunnelCard() {
    const { profile, profileError } = useAiUserRunner()
    const { closeChat } = useChatDock()
    const navigate = useNavigate()

    if (profileError) {
        return (
            <div className="flex gap-2 rounded-xl border border-error/40 bg-error/5 p-3 text-sm">
                <MdWarning className="mt-0.5 shrink-0 text-lg text-error" />
                <span>{profileError}</span>
            </div>
        )
    }
    if (!profile) {
        return <span className="loading loading-dots loading-sm text-base-content/40" />
    }
    if (profile.pipelines.length === 0) {
        return (
            <div className="flex gap-2 rounded-xl border border-warning/50 bg-warning/10 p-3">
                <MdWarning className="mt-0.5 shrink-0 text-lg text-warning" />
                <div className="text-sm">
                    <p className="font-medium">Sem funil próprio</p>
                    <p className="mt-0.5 text-xs text-base-content/70">
                        O usuário de IA precisa ser dono de um funil para trabalhar. Peça a quem
                        administra a conta para criar o funil e colocar este usuário como dono, em
                        Vendas &gt; Funis.
                    </p>
                </div>
            </div>
        )
    }
    return (
        <div className="rounded-xl border border-base-300 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-base-content/60">
                {profile.pipelines.length > 1 ? 'Funis' : 'Funil'}
            </p>
            <ul className="flex flex-col gap-1">
                {profile.pipelines.map(pipeline => (
                    <li key={pipeline.id} className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm">
                            {pipeline.name}
                            <span className="ml-1 text-xs text-base-content/50">
                                · {pipeline.stages.length} etapas
                            </span>
                        </span>
                        <button
                            type="button"
                            className="btn btn-ghost btn-xs gap-1"
                            onClick={() => {
                                closeChat()
                                navigate(`/sales/pipelines/${pipeline.id}`)
                            }}
                        >
                            <MdViewKanban />
                            Abrir
                        </button>
                    </li>
                ))}
            </ul>
            <p className="mt-2 text-xs text-base-content/60">
                {profile.wallets.length > 0
                    ? `Carteira${profile.wallets.length > 1 ? 's' : ''}: ${profile.wallets.map(wallet => wallet.name).join(', ')}`
                    : 'Sem carteira: a IA atende só as oportunidades do funil dela.'}
            </p>
        </div>
    )
}

function Controls() {
    const { status, start, pause, stopNow, profile } = useAiUserRunner()
    const hasFunnel = !!profile && profile.pipelines.length > 0
    return (
        <div className="flex gap-2">
            {status === 'idle' ? (
                <button
                    type="button"
                    className="btn btn-primary btn-sm flex-1 gap-1"
                    onClick={() => {
                        // O clique é a chance de pedir a permissão de notificação.
                        requestAttentionPermission()
                        start()
                    }}
                    disabled={!hasFunnel}
                    title={hasFunnel ? undefined : 'O usuário de IA precisa de um funil próprio'}
                >
                    <MdPlayArrow className="text-lg" />
                    Iniciar
                </button>
            ) : (
                <>
                    {status === 'running' && (
                        <button type="button" className="btn btn-outline btn-sm flex-1 gap-1" onClick={pause}>
                            <MdPause className="text-lg" />
                            Pausar
                        </button>
                    )}
                    <button type="button" className="btn btn-error btn-outline btn-sm flex-1 gap-1" onClick={stopNow}>
                        <MdStop className="text-lg" />
                        Parar já
                    </button>
                </>
            )}
            <HandoverSearch />
        </div>
    )
}

/**
 * Conversas movidas para a IA com o contato já calado (ex.: a triagem prometeu o valor e ele
 * ficou esperando) não têm mensagem nova que as traga: o botão as procura e ela assume.
 */
function HandoverSearch() {
    const { findHandovers, findingHandovers, profile } = useAiUserRunner()
    const hasFunnel = !!profile && profile.pipelines.length > 0
    return (
        <button
            type="button"
            className="btn btn-outline btn-sm btn-square shrink-0"
            onClick={() => void findHandovers()}
            disabled={findingHandovers || !hasFunnel}
            aria-label="Procurar conversas recebidas"
            title="Procurar conversas recebidas: as que passaram para você e em que você ainda não escreveu"
        >
            {findingHandovers ? <span className="loading loading-spinner loading-xs" /> : <MdManageSearch className="text-lg" />}
        </button>
    )
}

function SettingsCard({ attention }: { attention: ReturnType<typeof useAttentionPreference> }) {
    const { requireApproval, setRequireApproval } = useAiUserRunner()
    return (
        <div className="rounded-xl border border-base-300 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-base-content/60">
                Configurações
            </p>
            <label className="flex cursor-pointer items-start justify-between gap-3">
                <span>
                    <span className="block text-sm font-medium">Enviar só com minha aprovação</span>
                    <span className="mt-0.5 block text-xs text-base-content/60">
                        A resposta fica na lista "Aguardando sua aprovação" e a IA segue com as outras
                        conversas; ela só sai quando você clica em Enviar. Desligado, sai logo depois de
                        escrita. Vale só neste navegador.
                    </span>
                </span>
                <input
                    type="checkbox"
                    className="toggle toggle-primary toggle-sm mt-0.5 shrink-0"
                    checked={requireApproval}
                    onChange={event => setRequireApproval(event.target.checked)}
                />
            </label>
            <label className="mt-3 flex cursor-pointer items-start justify-between gap-3 border-t border-base-300 pt-3">
                <span>
                    <span className="block text-sm font-medium">Avisar quando eu estiver em outra aba</span>
                    <span className="mt-0.5 block text-xs text-base-content/60">
                        Som e notificação do navegador quando chega resposta para aprovar ou pergunta
                        nova, e a contagem no título da aba. Vale só neste navegador.
                    </span>
                </span>
                <input
                    type="checkbox"
                    className="toggle toggle-primary toggle-sm mt-0.5 shrink-0"
                    checked={attention.enabled}
                    onChange={event => attention.setEnabled(event.target.checked)}
                />
            </label>
        </div>
    )
}

function ApprovalItem({ entry, now }: { entry: PendingApproval; now: number }) {
    const { decideApproval } = useAiUserRunner()
    const [text, setText] = useState(entry.text)
    return (
        <li className="rounded-lg border border-primary/40 bg-primary/5 p-3">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{conversationLabel(entry.chatId, entry.chatName)}</p>
                    <p className={`text-xs ${waitTone(entry.createdAt, now)}`}>{waitedFor(entry.createdAt, now)}</p>
                </div>
                <OpenChatButton chatId={entry.chatId} />
            </div>
            <textarea
                className="textarea textarea-bordered textarea-sm mt-2 w-full whitespace-pre-wrap"
                rows={Math.min(8, Math.max(2, text.split('\n').length))}
                aria-label="Resposta da IA (pode editar antes de enviar)"
                value={text}
                onChange={event => setText(event.target.value)}
            />
            <div className="mt-2 flex gap-2">
                <button
                    type="button"
                    className="btn btn-primary btn-sm flex-1 gap-1"
                    disabled={!text.trim()}
                    onClick={() => decideApproval(entry.id, 'approve', text)}
                >
                    <MdCheck className="text-lg" />
                    Enviar
                </button>
                <button
                    type="button"
                    className="btn btn-ghost btn-sm flex-1 gap-1"
                    onClick={() => decideApproval(entry.id, 'discard')}
                >
                    <MdClose className="text-lg" />
                    Descartar
                </button>
            </div>
        </li>
    )
}

function ApprovalList() {
    const { approvals } = useAiUserRunner()
    const now = useNow()
    if (approvals.length === 0) return null
    return (
        <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-primary">
                Aguardando sua aprovação ({approvals.length})
            </p>
            <p className="mb-2 text-xs text-base-content/60">
                A mais antiga primeiro. Edite o texto aqui, se quiser: é ele que sai.
            </p>
            <ul className="flex flex-col gap-2">
                {approvals.map(entry => (
                    <ApprovalItem key={entry.id} entry={entry} now={now} />
                ))}
            </ul>
        </div>
    )
}

function CurrentCard() {
    const { current, assumeCurrent } = useAiUserRunner()
    if (!current) return null
    return (
        <div className="rounded-xl border border-base-300 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-base-content/60">Agora</p>
            <p className="mt-1 text-sm font-medium">{conversationLabel(current.chatId, current.chatName)}</p>
            <p className="mt-1 flex items-center gap-2 text-xs text-base-content/70">
                <span className="loading loading-dots loading-xs" />
                {STAGE_LABEL[current.stage]}
            </p>
            <button type="button" className="btn btn-link btn-xs mt-2 h-auto min-h-0 gap-1 px-0" onClick={assumeCurrent}>
                <MdPerson />
                Assumir esta conversa
            </button>
        </div>
    )
}

function WaitingItem({ entry, now }: { entry: AiUserPendingGuidance; now: number }) {
    const { answerGuidance } = useAiUserRunner()
    const [answer, setAnswer] = useState('')
    const since = new Date(entry.createdAt).getTime()
    const [sending, setSending] = useState(false)

    const submit = async () => {
        if (!answer.trim() || sending) return
        setSending(true)
        const ok = await answerGuidance(entry.id, answer)
        setSending(false)
        if (ok) setAnswer('')
    }

    return (
        <li className="rounded-lg border border-warning/50 bg-warning/10 p-3">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{conversationLabel(entry.chatId, entry.chat?.name ?? undefined)}</p>
                    <p className={`text-xs ${waitTone(since, now)}`}>{waitedFor(since, now)}</p>
                </div>
                <OpenChatButton chatId={entry.chatId} />
            </div>
            <p className="mt-1 whitespace-pre-wrap text-sm text-base-content/80">{entry.question}</p>
            <textarea
                className="textarea textarea-bordered textarea-sm mt-2 w-full"
                rows={2}
                placeholder="O que a IA deve saber ou fazer..."
                value={answer}
                onChange={event => setAnswer(event.target.value)}
                onKeyDown={event => {
                    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                        event.preventDefault()
                        void submit()
                    }
                }}
            />
            <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-xs text-base-content/50">O contato não recebe nada até você responder.</span>
                <button
                    type="button"
                    className="btn btn-primary btn-sm gap-1"
                    disabled={!answer.trim() || sending}
                    onClick={() => void submit()}
                >
                    {sending ? <span className="loading loading-spinner loading-xs" /> : <MdSend />}
                    Orientar
                </button>
            </div>
        </li>
    )
}

type ReasonFilter = 'all' | AiUserSupervisorReason

function WaitingList() {
    const { waiting } = useAiUserRunner()
    const now = useNow()
    const [filter, setFilter] = useState<ReasonFilter>('all')
    // A mais antiga primeiro: é a que está há mais tempo parada.
    const sorted = useMemo(
        () => [...waiting].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
        [waiting],
    )
    const counts = useMemo(() => {
        const byReason = new Map<AiUserSupervisorReason, number>()
        for (const entry of sorted) {
            const reason = supervisorReasonOf(entry.question)
            byReason.set(reason, (byReason.get(reason) ?? 0) + 1)
        }
        return byReason
    }, [sorted])
    // Filtro escolhido que esvaziou (a última pergunta daquele motivo foi respondida): volta a "Todas".
    const active: ReasonFilter = filter !== 'all' && !counts.has(filter) ? 'all' : filter
    if (waiting.length === 0) return null
    const visible = active === 'all' ? sorted : sorted.filter(entry => supervisorReasonOf(entry.question) === active)
    return (
        <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-warning">
                Esperando você ({waiting.length})
            </p>
            {counts.size > 1 && (
                <div className="mb-2 flex flex-wrap gap-1" role="group" aria-label="Filtrar por motivo">
                    <button
                        type="button"
                        className={`btn btn-xs ${active === 'all' ? 'btn-warning' : 'btn-ghost'}`}
                        aria-pressed={active === 'all'}
                        onClick={() => setFilter('all')}
                    >
                        Todas ({waiting.length})
                    </button>
                    {[...counts.entries()].map(([reason, count]) => (
                        <button
                            key={reason}
                            type="button"
                            className={`btn btn-xs ${active === reason ? 'btn-warning' : 'btn-ghost'}`}
                            aria-pressed={active === reason}
                            onClick={() => setFilter(reason)}
                        >
                            {SUPERVISOR_REASON_LABEL[reason]} ({count})
                        </button>
                    ))}
                </div>
            )}
            <ul className="flex flex-col gap-2">
                {visible.map(entry => (
                    <WaitingItem key={entry.id} entry={entry} now={now} />
                ))}
            </ul>
        </div>
    )
}

function QueueList() {
    const { queue } = useAiUserRunner()
    return (
        <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-base-content/60">
                Fila ({queue.length})
            </p>
            {queue.length === 0 ? (
                <p className="text-xs text-base-content/50">Nenhuma conversa esperando.</p>
            ) : (
                <ul className="flex flex-col gap-1">
                    {queue.slice(0, 8).map(item => (
                        <li key={item.chatId} className="truncate text-sm text-base-content/80">
                            {conversationLabel(item.chatId, item.chatName)}
                        </li>
                    ))}
                    {queue.length > 8 && (
                        <li className="text-xs text-base-content/50">e mais {queue.length - 8}</li>
                    )}
                </ul>
            )}
        </div>
    )
}

function Diary() {
    const { diary } = useAiUserRunner()
    return (
        <div className="flex min-h-[220px] flex-1 flex-col">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-base-content/60">Diário</p>
            {diary.length === 0 ? (
                <p className="text-xs text-base-content/50">
                    Tudo o que a IA ler, decidir e fizer aparece aqui.
                </p>
            ) : (
                <ul className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-1">
                    {diary.map(entry => (
                        <li key={entry.id} className="flex gap-2">
                            <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${DIARY_DOT[entry.kind]}`} />
                            <div className="min-w-0">
                                <p className="text-xs text-base-content/50">
                                    {timeFormat.format(entry.at)}
                                    {entry.chatId ? ` · #${entry.chatId}` : ''}
                                </p>
                                <p className="break-words text-sm">{entry.text}</p>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

/**
 * Painel do supervisor do Modo IA (ver docs/ai-user.md, "Tela do supervisor").
 *
 * Gaveta flutuante: fechada, é uma aba na borda direita com o estado da IA;
 * aberta, fica POR CIMA da tela e não toma espaço dela — a aplicação, com a
 * conversa no dock, é onde a IA trabalha e precisa da largura inteira.
 *
 * Abre sozinha quando há resposta esperando aprovação: é o único momento em que
 * o supervisor PRECISA agir. Fechar de novo é escolha dele.
 */
export function AiUserPanel() {
    const { user } = useAuth()
    const { status, approvals, waiting } = useAiUserRunner()
    const [open, setOpen] = useState(true)
    const [showSettings, setShowSettings] = useState(false)
    const attention = useAttentionPreference()

    // Resposta nova para aprovar abre a gaveta: é quando o supervisor PRECISA agir.
    const approvalsKey = approvals.map(entry => entry.id).join(',')
    const previousApprovalsKey = useRef(approvalsKey)
    useEffect(() => {
        const previous = new Set(previousApprovalsKey.current.split(',').filter(Boolean))
        previousApprovalsKey.current = approvalsKey
        if (approvals.some(entry => !previous.has(String(entry.id)))) setOpen(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [approvalsKey])

    // Aba em segundo plano: som, notificação e contagem no título.
    useSupervisorAttention({
        keys: [...approvals.map(entry => `a${entry.id}`), ...waiting.map(entry => `w${entry.id}`)],
        total: approvals.length + waiting.length,
        enabled: attention.enabled,
    })

    // Pergunta nova da IA abre a gaveta: a conversa está parada esperando você.
    const waitingKey = waiting.map(entry => entry.id).join(',')
    const previousWaitingKey = useRef(waitingKey)
    useEffect(() => {
        const previous = new Set(previousWaitingKey.current.split(',').filter(Boolean))
        const hasNew = waiting.some(entry => !previous.has(String(entry.id)))
        previousWaitingKey.current = waitingKey
        if (hasNew) setOpen(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waitingKey])

    const statusDot =
        status === 'running' ? 'bg-success' : status === 'pausing' ? 'bg-warning' : 'bg-base-content/30'

    return (
        <>
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="fixed right-0 top-1/2 z-50 flex -translate-y-1/2 flex-col items-center gap-2 rounded-l-xl border border-r-0 border-base-300 bg-base-100 px-2 py-3 shadow-lg transition hover:bg-base-200"
                    aria-label="Abrir o painel do Modo IA"
                    title="Abrir o painel do Modo IA"
                >
                    <MdSmartToy className="text-xl text-primary" />
                    <span className={`h-2.5 w-2.5 rounded-full ${statusDot}`} />
                    {approvals.length > 0 && (
                        <span className="badge badge-primary badge-sm animate-pulse" title="Respostas aguardando aprovação">
                            {approvals.length}
                        </span>
                    )}
                    {waiting.length > 0 && (
                        <span className="badge badge-warning badge-sm animate-pulse" title="Perguntas esperando você">
                            {waiting.length}
                        </span>
                    )}
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-base-content/60 [writing-mode:vertical-rl]">
                        Modo IA
                    </span>
                </button>
            )}

            {open && (
                <aside
                    className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[380px] flex-col gap-4 overflow-hidden border-l border-base-300 bg-base-100 p-4 shadow-2xl"
                    aria-label="Painel do Modo IA"
                >
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <MdSmartToy className="text-2xl text-primary" />
                            <div>
                                <p className="font-semibold leading-tight">Modo IA</p>
                                <p className="text-xs text-base-content/60">{user?.name}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className={`badge badge-sm ${STATUS_BADGE[status]}`}>{STATUS_LABEL[status]}</span>
                            <button
                                type="button"
                                className={`btn btn-ghost btn-sm btn-square ${showSettings ? 'btn-active' : ''}`}
                                aria-label="Configurações do Modo IA"
                                aria-expanded={showSettings}
                                title="Configurações do Modo IA"
                                onClick={() => setShowSettings(value => !value)}
                            >
                                <MdSettings className="text-lg" />
                            </button>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-square"
                                aria-label="Fechar o painel"
                                title="Fechar o painel (a IA continua trabalhando)"
                                onClick={() => setOpen(false)}
                            >
                                <MdChevronRight className="text-xl" />
                            </button>
                        </div>
                    </div>

                    {/* Só o corpo rola: com perguntas e fila longas, "Esperando você" ficava
                        cortado embaixo, sem scroll. O Diário mantém altura mínima e rolagem própria. */}
                    <div className="-mr-2 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-2">
                        {showSettings && <SettingsCard attention={attention} />}
                        <FunnelCard />
                        <Controls />
                        <ApprovalList />
                        <CurrentCard />
                        {/* A fila (com quem a IA está conversando) vem antes das perguntas. */}
                        <QueueList />
                        <WaitingList />
                        <div className="divider my-0" />
                        <Diary />
                    </div>
                </aside>
            )}
        </>
    )
}
